import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMultiTenant } from "@/hooks/useMultiTenant";
import { daysSinceLastPurchase } from "@/utils/customerRecency";

/** Result shape from RPC get_customer_product_history_by_document */
export interface CustomerProductPurchaseSummary {
  last_sale_number: string | null;
  last_sale_date: string | null;
  last_unit_price: number | null;
  last_quantity: number | null;
  total_quantity: number;
  total_value: number;
  sale_count: number;
  average_price: number;
  days_since_last_purchase: number | null;
}

export async function fetchCustomerProductPurchaseSummary(
  companyId: string,
  customerId: string,
  productId: string,
): Promise<CustomerProductPurchaseSummary | null> {
  const { data, error } = await supabase.rpc(
    "get_customer_product_history_by_document" as any,
    {
      p_company_id: companyId,
      p_customer_id: customerId,
      p_product_id: productId,
    },
  );

  if (error) {
    throw error;
  }

  const row = Array.isArray(data) ? data[0] : data;

  if (!row) {
    return null;
  }

  const daysSince = daysSinceLastPurchase(row.last_sale_date);

  return {
    last_sale_number: row.last_sale_number,
    last_sale_date: row.last_sale_date,
    last_unit_price: row.last_unit_price,
    last_quantity: row.last_quantity,
    total_quantity: row.total_quantity,
    total_value: row.total_value,
    sale_count: row.sale_count,
    average_price: row.average_price,
    days_since_last_purchase: daysSince,
  };
}

export function useCustomerProductHistory(
  customerId: string | undefined,
  productId: string | undefined,
) {
  const { companyId } = useMultiTenant();

  return useQuery({
    queryKey: ["customer-product-history", companyId, customerId, productId],
    queryFn: async (): Promise<CustomerProductPurchaseSummary | null> => {
      if (!companyId || !customerId || !productId) {
        return null;
      }

      return fetchCustomerProductPurchaseSummary(
        companyId,
        customerId,
        productId,
      );
    },
    enabled: !!companyId && !!customerId && !!productId,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Histórico por produto usando a mesma RPC do desktop ({@link useCustomerProductHistory}).
 * `undefined`: ainda carregando; `null`: sem histórico; objeto: igual ao modal.
 */
export function useCustomerProductHistoriesBulk(
  customerId: string | undefined,
  productIds: readonly string[],
  listEnabled: boolean,
) {
  const { companyId } = useMultiTenant();

  const idFingerprint = useMemo(() => {
    const u = [...new Set(productIds.filter((id): id is string => !!id))];
    u.sort();
    return u.join("|");
  }, [productIds]);

  const uniqueSortedIds = useMemo(() => {
    const u = [...new Set(productIds.filter((id): id is string => !!id))];
    u.sort();
    return u;
  }, [idFingerprint]);

  const queriesEnabled =
    !!listEnabled &&
    !!companyId &&
    !!customerId &&
    uniqueSortedIds.length > 0;

  const results = useQueries({
    queries: uniqueSortedIds.map((productId) => ({
      queryKey: ["customer-product-history", companyId, customerId, productId],
      queryFn: () =>
        fetchCustomerProductPurchaseSummary(
          companyId!,
          customerId!,
          productId,
        ),
      enabled: queriesEnabled,
      staleTime: 0,
      refetchOnMount: "always" as const,
    })),
  });

  const historyByProductId = useMemo(() => {
    const m = new Map<
      string,
      CustomerProductPurchaseSummary | null | undefined
    >();

    if (!queriesEnabled) {
      return m;
    }

    uniqueSortedIds.forEach((productId, i) => {
      const r = results[i];
      if (!r || !r.isFetched) {
        m.set(productId, undefined);
      } else if (r.isError) {
        m.set(productId, null);
      } else {
        m.set(productId, r.data ?? null);
      }
    });
    return m;
  }, [queriesEnabled, uniqueSortedIds, results]);

  return historyByProductId;
}
